const OPERATIONS_SETUP_ALIASES = {
  "Vehicle Segments": ["vehicle segments", "vehicle-segment", "vehicles"],
  "Custom Booking": ["custom booking", "custom-booking"],
  "Tour Package Management": ["tour package management", "packages"],
  "State Tax List": ["state tax list", "taxes"],
  "Terms and Conditions": ["terms and conditions"],
};

const GENERAL_ALIASES = {
  Bookings: ["bookings", "leads"],
  "Customer List": ["customer list", "customer management"],
  "Booking History": ["booking history", "customer management"],
  "Customer Policies": ["customer policies", "customer management"],
  "Create Report": ["create report", "reports"],
  "View Report": ["view report", "reports"],
  Stats: ["stats", "reports"],
  "Finance Customer": ["finance customer", "finance"],
  "Finance Driver": ["finance driver", "finance"],
  "Finance Vendor": ["finance vendor", "finance"],
  "Generate Invoice and Receipts": [
    "generate invoice and receipts",
    "finance",
  ],
  Vehicles: ["vehicles", "fleet management"],
  Drivers: ["drivers", "fleet management"],
  Vendors: ["vendors", "fleet management"],
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const getCanonicalOperationsSetupSubHeading = (subHeading) => {
  const current = normalize(subHeading);

  for (const [canonical, aliases] of Object.entries(OPERATIONS_SETUP_ALIASES)) {
    if (aliases.includes(current)) {
      return canonical;
    }
  }

  return String(subHeading || "").trim();
};

const getCanonicalGeneralSubHeading = (subHeading) => {
  const current = normalize(subHeading);

  for (const [canonical, aliases] of Object.entries(GENERAL_ALIASES)) {
    if (aliases.includes(current)) {
      return canonical;
    }
  }

  return String(subHeading || "").trim();
};

const getCanonicalSubHeadingByHeading = (heading, subHeading) => {
  const headingKey = normalize(heading);

  if (headingKey === "operations setup") {
    return getCanonicalOperationsSetupSubHeading(subHeading);
  }

  if (headingKey === "general") {
    return getCanonicalGeneralSubHeading(subHeading);
  }

  return String(subHeading || "").trim();
};

const getAliasesForCanonicalByHeading = (heading, canonical) => {
  const headingKey = normalize(heading);

  if (headingKey === "operations setup") {
    return OPERATIONS_SETUP_ALIASES[canonical] || [normalize(canonical)];
  }

  if (headingKey === "general") {
    return GENERAL_ALIASES[canonical] || [normalize(canonical)];
  }

  return [normalize(canonical)];
};

const getSubHeadingCandidates = (heading, subHeading) => {
  const canonical = getCanonicalSubHeadingByHeading(heading, subHeading);
  const aliases = getAliasesForCanonicalByHeading(heading, canonical);

  const titleCaseAliases = aliases.map((alias) => {
    const aliasSource =
      normalize(heading) === "operations setup"
        ? Object.keys(OPERATIONS_SETUP_ALIASES)
        : normalize(heading) === "general"
          ? Object.keys(GENERAL_ALIASES)
          : [];

    const found = aliasSource.find((key) => normalize(key) === alias);
    return found || alias;
  });

  return Array.from(new Set([canonical, ...titleCaseAliases, String(subHeading || "").trim()]));
};

const normalizeRoleAccessSections = (roleAccess = []) => {
  return (Array.isArray(roleAccess) ? roleAccess : []).map((section) => {
    const heading = String(section?.heading || "").trim();
    const types = Array.isArray(section?.types) ? section.types : [];

    if (normalize(heading) === "general") {
      const expandedTypes = [];

      types.forEach((type) => {
        const rawSubHeading = String(type?.subHeading || "").trim();

        if (normalize(rawSubHeading) === "fleet management") {
          ["Vehicles", "Drivers", "Vendors"].forEach((subHeading) => {
            expandedTypes.push({
              ...type,
              subHeading,
            });
          });
          return;
        }

        if (normalize(rawSubHeading) === "reports") {
          ["Create Report", "View Report", "Stats"].forEach((subHeading) => {
            expandedTypes.push({
              ...type,
              subHeading,
            });
          });
          return;
        }

        if (normalize(rawSubHeading) === "customer management") {
          ["Customer List", "Booking History", "Customer Policies"].forEach(
            (subHeading) => {
              expandedTypes.push({
                ...type,
                subHeading,
              });
            },
          );
          return;
        }

        if (normalize(rawSubHeading) === "finance") {
          [
            "Finance Customer",
            "Finance Driver",
            "Finance Vendor",
            "Generate Invoice and Receipts",
          ].forEach((subHeading) => {
            expandedTypes.push({
              ...type,
              subHeading,
            });
          });
          return;
        }

        expandedTypes.push({
          ...type,
          subHeading: getCanonicalSubHeadingByHeading(heading, rawSubHeading),
        });
      });

      return {
        ...section,
        heading,
        types: expandedTypes,
      };
    }

    return {
      ...section,
      heading,
      types: types
        .map((type) => ({
            ...type,
            subHeading: getCanonicalSubHeadingByHeading(
              heading,
              type?.subHeading,
            ),
          })),
    };
  });
};

export {
  getCanonicalOperationsSetupSubHeading,
  getSubHeadingCandidates,
  normalizeRoleAccessSections,
};
